import React from 'react';
import { Users, Target, Activity, AlertTriangle } from 'lucide-react';
import type { DeptUnit } from '../../services/executiveDeptUnitsService';

const ALL_UNITS = '__all__';

interface DeptUnitsMatrixProps {
  units: DeptUnit[];
  isLoading?: boolean;
  isFallback?: boolean;
  selectedDeptId: string;
  onSelectDept: (deptId: string) => void;
}

function InitialsAvatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold text-white">
      {initials}
    </span>
  );
}

function PeopleBadge({ count }: { count: number }) {
  return <span className="text-[10px] text-[var(--text-secondary)]">{count} pessoas</span>;
}

export const DeptUnitsMatrix: React.FC<DeptUnitsMatrixProps> = ({
  units,
  isLoading,
  isFallback,
  selectedDeptId,
  onSelectDept,
}) => (
  <div className="rounded-sm border border-[var(--border-default)]/70 bg-[var(--bg-surface)] overflow-hidden">
    <div className="flex items-center justify-between border-b border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/80 px-4 py-2">
      <div className="flex items-center gap-2">
        <Users size={12} className="text-[var(--text-secondary)]" />
        <span className="text-[9px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
          Unidades de Valor / Departamentos
        </span>
        {isFallback && (
          <span className="rounded-sm border border-amber-400/20 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-medium text-amber-600">
            Estrutura padrão
          </span>
        )}
      </div>
      <span className="text-[10px] text-[var(--text-muted)]">
        {isLoading ? 'Carregando…' : `${units.length} setor${units.length !== 1 ? 'es' : ''}`}
      </span>
    </div>

    {isFallback && !isLoading && (
      <div className="flex items-center gap-2 border-b border-amber-400/20 bg-amber-500/5 px-4 py-2 text-[10px] text-amber-700 dark:text-amber-400">
        <AlertTriangle size={11} className="shrink-0" />
        Configure departamentos em Organização → Estrutura para dados reais por setor.
      </div>
    )}

    {isLoading ? (
      <div className="py-8 text-center text-[11px] text-[var(--text-muted)] animate-pulse">
        Carregando estrutura organizacional…
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[11px]">
          <thead>
            <tr className="bg-[var(--bg-elevated)]">
              <th className="px-3 py-2 text-left text-[9px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Setor</th>
              <th className="px-3 py-2 text-left text-[9px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Gestor Vinculado</th>
              <th className="px-3 py-2 text-center text-[9px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Time</th>
              <th className="px-3 py-2 text-center text-[9px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Projetos</th>
              <th className="px-3 py-2 text-left text-[9px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Pessoas</th>
              <th className="px-3 py-2 text-center text-[9px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-default)]/50">
            <tr
              className={`cursor-pointer transition-colors ${
                selectedDeptId === ALL_UNITS ? 'bg-blue-500/5' : 'hover:bg-[var(--button-hover)]/40'
              }`}
              onClick={() => onSelectDept(ALL_UNITS)}
            >
              <td className="px-3 py-2 font-medium text-[var(--text-primary)]">Todas as Unidades</td>
              <td className="px-3 py-2 text-[var(--text-muted)]">Visão consolidada</td>
              <td className="px-3 py-2 text-center text-[var(--text-muted)]">—</td>
              <td className="px-3 py-2 text-center text-[var(--text-muted)]">—</td>
              <td className="px-3 py-2 text-[var(--text-muted)]">—</td>
              <td className="px-3 py-2 text-center text-[var(--text-muted)]">—</td>
            </tr>
            {units.map((unit) => {
              const selected = selectedDeptId === unit.department.id;
              const managerName = unit.manager?.user_name ?? null;
              return (
                <tr
                  key={unit.department.id}
                  className={`cursor-pointer transition-colors ${
                    selected ? 'bg-blue-500/5' : 'hover:bg-[var(--button-hover)]/40'
                  }`}
                  onClick={() => onSelectDept(unit.department.id)}
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: unit.department.color ?? '#64748b' }}
                      />
                      <span className="font-medium text-[var(--text-primary)]">{unit.department.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {managerName ? (
                      <div className="flex items-center gap-1.5">
                        <InitialsAvatar name={managerName} />
                        <span className="truncate text-[var(--text-secondary)] max-w-[140px]">{managerName}</span>
                      </div>
                    ) : unit.members.length > 0 ? (
                      <span className="text-[10px] text-amber-600">Definir gestor no organograma</span>
                    ) : (
                      <span className="text-[10px] text-[var(--text-muted)]">Sem membros</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className="inline-flex items-center gap-1 text-[var(--text-secondary)]">
                      <Users size={10} />
                      {unit.fteCount}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className="inline-flex items-center gap-1 text-[var(--text-secondary)]">
                      <Target size={10} />
                      {unit.projects.length}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <PeopleBadge count={unit.fteCount} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    {unit.actionCount > 0 ? (
                      <span className="inline-flex items-center gap-1 font-semibold text-amber-600">
                        <Activity size={10} />
                        {unit.actionCount}
                        {unit.overdueCount > 0 && (
                          <span className="text-red-500">({unit.overdueCount} atras.)</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-[10px] text-[var(--text-muted)]">0</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export { ALL_UNITS as OPS_ALL_UNITS };
